import { Fragment, useEffect, useState, type ReactNode } from 'react';

export interface ContextMenuAction {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  /** Renders a horizontal rule above this item (e.g. before destructive actions). */
  separatorBefore?: boolean;
}

interface ContextMenuProps {
  children: ReactNode;
  actions: ContextMenuAction[];
  className?: string;
}

export function ContextMenu({ children, actions, className }: ContextMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  return (
    <div
      className={className}
      onContextMenu={(event) => {
        event.preventDefault();
        setPosition({ x: event.clientX, y: event.clientY });
        setOpen(true);
      }}
    >
      {children}
      {open && (
        <div
          style={{ left: position.x, top: position.y }}
          className="fixed z-50 min-w-[180px] rounded-md border border-border bg-card p-1 shadow-lg"
        >
          {actions.map((action, index) => (
            <Fragment key={`${action.label}-${index}`}>
              {action.separatorBefore ? (
                <div
                  role="separator"
                  className="my-1 h-px bg-card-hover"
                  aria-hidden
                />
              ) : null}
              <button
                type="button"
                className={`block w-full rounded px-3 py-2 text-left text-sm hover:bg-card-hover dark:hover:bg-card-hover ${
                  action.destructive ? 'text-danger' : 'text-muted dark:text-foreground'
                }`}
                onClick={() => {
                  action.onClick();
                  setOpen(false);
                }}
              >
                {action.label}
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
